/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { ModalConfirm, Spinner, Text } from "@nordcloud/gnui";
import { OvDropdown } from "~/components/Forms/OvDropdown";
import { InlineEditForm } from "~/components/InlineEdit/InlineEditForm";
import { usePDCustomers } from "~/hooks/usePDCustomers";
import { Application, UpdateApplicationData } from "~/models";

type Props = {
  application: Application;
  updateApplication: (data: UpdateApplicationData) => Promise<void>;
  canEdit: boolean;
};

function PDServicesCustomerDropdown({
  application,
  updateApplication,
  canEdit,
}: Props) {
  const [value, setValue] = React.useState(application.pdCustomer);
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [clearMode, setClearMode] = React.useState(false);

  const { customers } = usePDCustomers();

  React.useEffect(() => {
    setValue(application.pdCustomer);
  }, [setValue, application]);

  const updatePDCustomer = async (clear = false) => {
    setConfirmModalOpen(false);
    await updateApplication({ pdCustomer: clear ? null : value });
  };

  const checkUpdatePDCustomer = async () => {
    if (application.pdServices.length) {
      setClearMode(false);
      setConfirmModalOpen(true);
    } else {
      return updatePDCustomer();
    }
  };

  const clearPDCustomer = async () => {
    if (application.pdServices.length) {
      setClearMode(true);
      setConfirmModalOpen(true);
    } else {
      return updatePDCustomer(true);
    }
  };

  if (!customers.length) {
    return <Spinner size="sm" />;
  }

  const display = (
    <Text m="0">
      {application.pdCustomer
        ? `${customers.find((c) => c.id === application.pdCustomer)?.name}`
        : "–"}
    </Text>
  );

  if (!canEdit) {
    return display;
  }

  return (
    <>
      <InlineEditForm
        onSubmit={checkUpdatePDCustomer}
        onClear={clearPDCustomer}
        display={display}
      >
        <OvDropdown
          width="15rem"
          onChange={(c: string) => setValue(c)}
          value={value}
          name="pdCustomer"
          options={customers.map((c) => ({ value: c.id, label: c.name }))}
          placeholder="PagerDuty customer"
        />
      </InlineEditForm>

      <ModalConfirm
        onClose={() => setConfirmModalOpen(false)}
        isOpen={confirmModalOpen}
        confirm={() => updatePDCustomer(clearMode)}
        actionLabel="Change"
        contentLabel="Change PagerDuty customer"
      >
        <Text color="warning">
          Changing the customer will remove all assigned services. Proceed?
        </Text>
      </ModalConfirm>
    </>
  );
}

export { PDServicesCustomerDropdown };
